import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Avatar,
  Tooltip,
  Dialog,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Straighten as MeasurementIcon,
  DragIndicator as DragIcon,
  Restore as RestoreIcon,
  VisibilityOff as HiddenIcon,
  DeleteForever as DeleteForeverIcon,
  Close as RemoveIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getMeasurementDefinitions,
  deleteMeasurementDefinition,
  getHiddenMeasurementDefinitions,
  restoreMeasurementDefinition,
  getMeasurementGenderGrid,
  setMeasurementGenders,
  removeMeasurementFromGender,
  reorderMeasurementGenderGrid,
  createMeasurementFormula,
  formulaLabel,
  MEASUREMENT_FORMULAS,
} from "../services/MeasurementDefinitionService";
import { useMessageContext } from "../contexts/MessageContext";
import { useCompany } from "../contexts/CompanyContext";
import { useAuth } from "../contexts/AuthContext";
import { t } from "../componenets/dashboard/coach/coachTokens";
import StatusChip from "../componenets/ui/StatusChip";
import { RTL } from "../cache/CacheRtl";
import DeleteMeasurementDefinitionDialog from "../componenets/measurements/DeleteMeasurementDefinitionDialog";
import { unitLabel } from "../componenets/measurements/measurementUnits";
import { imageUrl } from "../utils/imageUrl";

// Views: ALL (כללי) is the catalog — the only place to add/hide/restore/permanently-delete.
// MALE/FEMALE are arrangement-only (pick which measurements appear + drag to sort).
const VIEWS = [
  { key: "ALL", label: "כללי", Icon: ViewListIcon },
  { key: "MALE", label: "גברים", Icon: MaleIcon },
  { key: "FEMALE", label: "נשים", Icon: FemaleIcon },
];

const genderLabel = (g) => (g === "MALE" ? "גברים" : "נשים");

/** Small M / F membership indicators for a catalog row. */
const GenderBadges = ({ genders }) => {
  const set = new Set(genders || []);
  const badge = (on, Icon, title) => (
    <Tooltip title={title}>
      <Box sx={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: t.rxs,
        background: on ? t.greenLight : t.bg,
        color: on ? t.greenText : t.t3,
        border: `1px solid ${on ? "rgba(1,187,167,.25)" : t.border}`,
        opacity: on ? 1 : 0.5,
      }}>
        <Icon sx={{ fontSize: 14 }} />
      </Box>
    </Tooltip>
  );
  return (
    <Box sx={{ display: "inline-flex", gap: "4px" }}>
      {badge(set.has("MALE"), MaleIcon, set.has("MALE") ? "משויך לגברים" : "לא משויך לגברים")}
      {badge(set.has("FEMALE"), FemaleIcon, set.has("FEMALE") ? "משויך לנשים" : "לא משויך לנשים")}
    </Box>
  );
};

/** A single draggable measurement card in the per-gender grid (@dnd-kit sortable). */
const SortableMeasurementCard = ({ item, index, gender, otherGender, both, lockReason, onEdit, onToggleGender, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const OtherIcon = otherGender === "MALE" ? MaleIcon : FemaleIcon;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 2 : "auto",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        position: "relative",
        display: "flex", flexDirection: "column", gap: "8px",
        padding: "10px 12px", borderRadius: t.rs, background: t.white,
        border: `1px solid ${isDragging ? t.green : t.border}`,
        boxShadow: isDragging ? "0 8px 20px rgba(1,187,167,.22)" : "none",
        cursor: "grab", touchAction: "none", userSelect: "none",
        "&:hover": { borderColor: t.green },
        "&:hover .cardActions": { opacity: 1 },
      }}
    >
      {/* Header: order badge + image + name + gender toggle */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", color: t.t3, flexShrink: 0 }}>
          <DragIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontSize: "10px", fontWeight: 700, ml: "1px" }}>{index + 1}</Typography>
        </Box>
        <Box sx={{ width: 26, height: 26, borderRadius: t.rxs || "6px", background: t.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
          {item.imageUrl
            ? <img src={imageUrl(item.imageUrl, 30)} alt={item.nameHeb || item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <MeasurementIcon sx={{ fontSize: 15, color: t.t2 }} />}
        </Box>
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: t.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {item.nameHeb || item.name}
        </Typography>
        {/* Narrowing to this gender alone would drop the measurement from the OTHER gender's grid,
            which is refused while a formula lives there — so the toggle is locked in that direction
            only (adding the other gender is always fine). */}
        <Tooltip title={both && lockReason
          ? lockReason
          : both ? `משויך לשני המגדרים — לחץ כדי להשאיר רק ${genderLabel(gender)}` : `לחץ כדי לשייך גם ל${genderLabel(otherGender)}`}>
          <span>
          <IconButton
            size="small"
            disabled={both && !!lockReason}
            aria-label={both ? `הסר מ${genderLabel(otherGender)}` : `שייך גם ל${genderLabel(otherGender)}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onToggleGender(); }}
            sx={{
              p: "3px", flexShrink: 0,
              color: both ? t.greenText : t.t3,
              background: both ? t.greenLight : "transparent",
              "&:hover": { background: t.greenLight },
            }}
          >
            <OtherIcon sx={{ fontSize: 15 }} />
          </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Footer: unit + edit / remove-from-gender actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "11px", color: t.t3 }}>{unitLabel(item.unit)}</Typography>
        <Box className="cardActions" sx={{ display: "flex", alignItems: "center", gap: "4px", opacity: 0, transition: "opacity .12s" }}>
          <IconButton
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            sx={{ p: "3px", color: t.green, "&:hover": { background: t.greenLight } }}
          >
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <Tooltip title={lockReason || (both
            ? `הסר מ${genderLabel(gender)} — הנתונים יישמרו, המדד יישאר ב${genderLabel(otherGender)} ובלשונית כללי`
            : `זהו המגדר היחיד של המדד — הסרה תסתיר את המדד (ניתן לשחזר בלשונית כללי)`)}>
            <Box
              component="button"
              disabled={!!lockReason}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); if (!lockReason) onRemove(); }}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "3px",
                height: 24, px: "8px", borderRadius: t.rxs,
                background: t.bg, color: t.t2, border: `1px solid ${t.border}`,
                fontSize: "11px", fontWeight: 600, fontFamily: "inherit",
                cursor: lockReason ? "not-allowed" : "pointer", transition: "all .14s",
                "&:hover": lockReason ? {} : { background: t.redLight, color: t.red, borderColor: `${t.red}44` },
                "&:disabled": { opacity: 0.55 },
              }}
            >
              <RemoveIcon sx={{ fontSize: 13 }} />
              הסר ממגדר זה
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

/** A single row in the כללי catalog table. */
/**
 * Why this measurement can't be hidden or deleted, in Hebrew — or null when it can.
 *
 * Derived from the catalog the screen already loaded (each computed definition carries its two
 * source ids), so the lock is visible on the row itself instead of only arriving as a 409 after the
 * person has already clicked through a confirmation dialog.
 */
const formulaLockReason = (def, lockedBy) => {
  const lock = lockedBy.get(def.id);
  return lock ? `לא ניתן להסתיר או למחוק — מדידה זו משמשת את "${lock}"` : null;
};

const CatalogRow = ({ def, hidden, lockReason, onEdit, onHide, onRestore, onPurge }) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "8px 12px", borderRadius: t.rs,
      background: hidden ? t.bg : t.white,
      border: `1px solid ${hidden ? t.border : t.borderLight}`,
    }}
  >
    <Avatar src={imageUrl(def.imageUrl, 32)} sx={{ width: 32, height: 32, bgcolor: t.bg, color: t.t3 }} variant="rounded">
      <MeasurementIcon sx={{ fontSize: 16 }} />
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {def.nameHeb || def.name}
      </Typography>
      <Typography sx={{ fontSize: "11.5px", color: t.t3 }}>{unitLabel(def.unit)}</Typography>
    </Box>

    {/* A computed measurement looks like any other row otherwise, and the difference matters:
        nobody enters a value into it. */}
    {def.computed && <StatusChip label="מחושב" variant="info" />}

    {!hidden && <GenderBadges genders={def.genders} />}

    <Box sx={{ width: 74, display: "flex", justifyContent: "center" }}>
      {hidden
        ? <StatusChip label="מוסתר" variant="default" />
        : <StatusChip label="פעיל" variant="success" />}
    </Box>

    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {hidden ? (
        <>
          <Tooltip title="שחזר מדד">
            <Box
              component="button"
              onClick={onRestore}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                background: t.greenLight, color: t.greenText, height: 30, px: "12px", borderRadius: t.rs,
                fontSize: "12px", fontWeight: 600, border: `1px solid rgba(1,187,167,.25)`,
                cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
                "&:hover": { background: t.green, color: "#fff" },
              }}
            >
              <RestoreIcon sx={{ fontSize: 15 }} />
              שחזר
            </Box>
          </Tooltip>
          {/* A hidden definition can still be a formula's input (hiding it was refused, but it may
              have been mapped while already hidden), so the purge button honors the lock too. */}
          <Tooltip title={lockReason || "מחיקה לצמיתות"}>
            <span>
              {/* Explicit aria-label: the <span> the Tooltip now needs would otherwise take the
                  accessible name with it, leaving the button nameless to a screen reader. */}
              <IconButton size="small" onClick={onPurge} disabled={!!lockReason}
                aria-label="מחיקה לצמיתות"
                sx={{ color: t.red, "&:hover": { background: t.redLight } }}>
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : (
        <>
          <Tooltip title="עריכה">
            <IconButton size="small" onClick={onEdit} sx={{ color: t.green, "&:hover": { background: t.greenLight } }}>
              <EditIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
          {/* The <span> is required: MUI's Tooltip needs a non-disabled child to receive hover, and
              the explanation is the whole point of disabling the button. */}
          <Tooltip title={lockReason || "הסתר מדד"}>
            <span>
              <IconButton size="small" onClick={onHide} disabled={!!lockReason}
                aria-label="הסתר מדד"
                sx={{ color: t.t2, "&:hover": { background: t.redLight, color: t.red } }}>
                <HiddenIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Box>
  </Box>
);

const MeasurementDefinitionsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notificationHandler } = useMessageContext();
  const { company } = useCompany();
  const { isSuperAdmin } = useAuth();
  const canCreate = !isSuperAdmin() || !!company?.id;

  // Restored from router state when we arrive back from a measurement's edit page, so saving an
  // edit returns to the tab it was opened from rather than dumping everyone on כללי. The tab is
  // component state, so a plain navigate back would always reset it.
  const [view, setView] = useState(location.state?.view || "ALL"); // ALL (כללי) | MALE | FEMALE
  const isGenderView = view === "MALE" || view === "FEMALE";
  const gender = view; // when isGenderView, this is the gender key

  const [grid, setGrid] = useState([]); // ordered items for the current gender
  const [catalog, setCatalog] = useState([]); // full active company catalog
  const [hidden, setHidden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, definition: null });
  const [purgeDialog, setPurgeDialog] = useState({ open: false, definition: null, busy: false });

  // Drag reorder state — @dnd-kit manages the active-drag internally; we only track saving.
  const [savingOrder, setSavingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadGrid = (g, companyId) => getMeasurementGenderGrid(g, companyId).then((res) => res.data || []);

  const load = () => {
    setLoading(true);
    // The gender grid is only needed on a gender view; catalog + hidden feed the כללי table
    // (and catalog powers the "not assigned to this gender" list on gender views).
    Promise.all([
      isGenderView ? loadGrid(gender, company?.id) : Promise.resolve([]),
      getHiddenMeasurementDefinitions(company?.id),
      getMeasurementDefinitions(company?.id),
    ]).then(([gridData, hiddenRes, catalogRes]) => {
      setGrid(gridData);
      setHidden(hiddenRes.data || []);
      setCatalog(catalogRes.data || []);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, view]);

  // --- כללי catalog rows: active (tagged hidden:false) + hidden (tagged hidden:true) ---
  const catalogRows = useMemo(() => [
    ...catalog.map((d) => ({ def: d, hidden: false })),
    ...hidden.map((d) => ({ def: d, hidden: true })),
  ], [catalog, hidden]);

  /**
   * definitionId -> the name of the computed measurement that uses it, for the rows the server will
   * refuse to hide or delete.
   *
   * Built only from ACTIVE computed measurements, matching the server guard exactly: hiding the
   * formula releases its inputs, so a hidden one must not keep them locked on screen.
   */
  const lockedByFormula = useMemo(() => {
    const map = new Map();
    catalog.filter((d) => d.formula).forEach((f) => {
      const name = f.nameHeb || f.name || formulaLabel(f.formula);
      [f.waistDefinitionId, f.neckDefinitionId].forEach((sourceId) => {
        if (sourceId != null) map.set(sourceId, name);
      });
    });
    return map;
  }, [catalog]);

  /** The company's computed measurement, if it has an active one — at most one can exist. */
  const activeFormula = useMemo(
    () => catalog.find((d) => d.formula === MEASUREMENT_FORMULAS.CIRCUMFERENCE_BODY_FAT) || null,
    [catalog],
  );

  /**
   * The lock as it applies to THIS gender's grid: removing an input from a gender is only refused
   * while the formula is in that same gender. The formula's membership in the grid on screen is
   * simply whether it is one of the cards, so this stays exactly as permissive as the server.
   */
  const gridLockReason = (item) => {
    const lock = lockedByFormula.get(item.id);
    if (!lock) return null;
    const formulaInThisGrid = grid.some((g) => g.computed);
    return formulaInThisGrid ? `לא ניתן להסיר — מדידה זו משמשת את "${lock}"` : null;
  };

  const [addingFormula, setAddingFormula] = useState(false);

  /**
   * Add נוסחת היקפים and go straight to its edit page: a formula with no inputs mapped shows no
   * value, so landing on the picker is the rest of the same action rather than a separate chore.
   */
  const handleAddFormula = async () => {
    setAddingFormula(true);
    const res = await createMeasurementFormula(MEASUREMENT_FORMULAS.CIRCUMFERENCE_BODY_FAT);
    setAddingFormula(false);
    if (!res.success) {
      notificationHandler?.error(res.message);
      return;
    }
    notificationHandler?.success(res.message);
    if (res.data?.id) {
      navigate(`/app/company-measurements/${res.data.id}`, {state: {from: view}});
    } else {
      load();
    }
  };

  // Active measurements NOT in the current gender's grid (they're active in the company, so they
  // must be in the OTHER gender) — surfaced so a coach can re-add them to this gender in one click.
  const notInThisGender = useMemo(() => {
    if (!isGenderView) return [];
    const inGrid = new Set(grid.map((g) => g.id));
    return catalog.filter((d) => !inGrid.has(d.id));
  }, [catalog, grid, isGenderView]);

  const handleAddToGender = async (def) => {
    // Active + not in this gender ⇒ it's in the other gender only; adding this one makes it BOTH.
    const res = await setMeasurementGenders(def.id, ["MALE", "FEMALE"], company?.id);
    if (res.success) {
      notificationHandler.success(`נוסף ל${genderLabel(gender)}`);
      load();
    } else {
      notificationHandler.error(res.message);
    }
  };

  const handleDelete = async (force) => {
    const res = await deleteMeasurementDefinition(deleteDialog.definition.id, force);
    if (res.success) {
      notificationHandler.success(res.message);
      setDeleteDialog({ open: false, definition: null });
      load();
    } else {
      notificationHandler.error(res.message);
    }
  };

  const handleRestore = async (def) => {
    const res = await restoreMeasurementDefinition(def.id, company?.id);
    if (res.success) {
      notificationHandler.success(res.message);
      load();
    } else {
      notificationHandler.error(res.message);
    }
  };

  const handlePurge = async () => {
    setPurgeDialog((p) => ({ ...p, busy: true }));
    const res = await deleteMeasurementDefinition(purgeDialog.definition.id, true);
    if (res.success) {
      notificationHandler.success("המדד וכל הנתונים נמחקו לצמיתות");
      setPurgeDialog({ open: false, definition: null, busy: false });
      load();
    } else {
      setPurgeDialog((p) => ({ ...p, busy: false }));
      notificationHandler.error(res.message);
    }
  };

  // Toggle a card's membership in the OTHER gender (this grid's gender always stays on —
  // removing it from the current grid would make it vanish from view, so we toggle the other one).
  const otherGender = gender === "MALE" ? "FEMALE" : "MALE";
  const isBoth = (item) => (item.genders || []).length === 2;

  // "Remove from this gender" chip. If it's in both genders, just un-associate from THIS gender
  // (data kept). If this is its ONLY gender, removing it is equivalent to HIDING the measurement,
  // so open the hide / permanently-delete dialog.
  const handleRemoveFromGrid = async (item) => {
    if (isBoth(item)) {
      const res = await removeMeasurementFromGender(item.id, gender, company?.id);
      if (res.success) {
        notificationHandler.success(`הוסר מ${genderLabel(gender)} (הנתונים נשמרו)`);
        setGrid((prev) => prev.filter((it) => it.id !== item.id));
      } else {
        notificationHandler.error(res.message);
      }
    } else {
      // Last gender — removing it hides the measurement. Let the admin confirm (hide vs. delete).
      setDeleteDialog({ open: true, definition: item });
    }
  };

  const handleToggleOtherGender = async (item) => {
    const nextGenders = isBoth(item) ? [gender] : [gender, otherGender];
    const res = await setMeasurementGenders(item.id, nextGenders, company?.id);
    if (res.success) {
      notificationHandler.success(isBoth(item) ? `הוסר מ${genderLabel(otherGender)}` : "עודכן לשני המגדרים");
      // Optimistic: update this item's genders in place (order in current grid unchanged).
      setGrid((prev) => prev.map((it) => (it.id === item.id ? { ...it, genders: nextGenders } : it)));
    } else {
      notificationHandler.error(res.message);
    }
  };

  // --- Drag reorder within the current gender grid (@dnd-kit, grid-aware) ---
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = grid.findIndex((it) => it.id === active.id);
    const to = grid.findIndex((it) => it.id === over.id);
    if (from === -1 || to === -1) return;

    const reordered = arrayMove(grid, from, to);
    setGrid(reordered); // optimistic

    setSavingOrder(true);
    const items = reordered.map((it, i) => ({ definitionId: it.id, displayOrder: i }));
    const res = await reorderMeasurementGenderGrid(gender, items, company?.id);
    setSavingOrder(false);
    if (!res.success) {
      notificationHandler.error(res.message);
      load(); // revert to server truth
    }
  };

  const tabBtn = (active) => ({
    display: "inline-flex", alignItems: "center", gap: "6px",
    height: 34, px: "16px", borderRadius: t.rs, fontSize: "13px", fontWeight: 700,
    border: `1.5px solid ${active ? t.green : t.border}`,
    background: active ? t.green : t.white, color: active ? "#fff" : t.t2,
    cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
  });

  const gridEmpty = !loading && isGenderView && grid.length === 0;
  const catalogEmpty = !loading && !isGenderView && catalogRows.length === 0;

  const subtitle = isGenderView
    ? `סדר את המדדים ל${genderLabel(gender)} — גרור לשינוי סדר, לחץ על סמל המגדר כדי לשייך גם ל${genderLabel(otherGender)}. הוספה, הסתרה ומחיקה — בלשונית כללי.`
    : "כל מדדי הגוף של החברה. כאן מוסיפים, מסתירים, משחזרים ומוחקים לצמיתות. שיוך לפי מגדר וסידור — בלשוניות גברים / נשים.";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <DeleteMeasurementDefinitionDialog
        open={deleteDialog.open}
        definition={deleteDialog.definition}
        companyId={company?.id}
        onClose={() => setDeleteDialog({ open: false, definition: null })}
        onConfirm={handleDelete}
      />

      {/* Title row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px" }}>
            מדדי גוף
          </Typography>
          <Typography sx={{ fontSize: "13px", color: t.t2, mt: 0.25, maxWidth: 720 }}>
            {subtitle}
          </Typography>
        </Box>
        {!isGenderView && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* Offered only while the company has none — at most one can exist, and the server would
              hand back the existing row anyway, which would read as a no-op. */}
          {!loading && !activeFormula && (
            <Box
              component="button"
              onClick={() => canCreate && !addingFormula && handleAddFormula()}
              disabled={!canCreate || addingFormula}
              title={!canCreate ? "בחר חברה כדי ליצור מדד" : "מדד שמחושב אוטומטית מהיקף הבטן והצוואר"}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                background: t.white, color: t.green, height: 34, px: "15px", borderRadius: t.rs,
                fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
                border: `1px solid ${t.green}55`, cursor: canCreate ? "pointer" : "not-allowed",
                fontFamily: "inherit", transition: "all .14s",
                "&:hover": canCreate ? { background: t.greenLight } : {},
                "&:disabled": { background: t.bg, color: t.t3, borderColor: t.border, cursor: "not-allowed" },
              }}
            >
              {addingFormula
                ? <CircularProgress size={14} sx={{ color: t.green }} />
                : <AddIcon sx={{ fontSize: 16 }} />}
              {formulaLabel(MEASUREMENT_FORMULAS.CIRCUMFERENCE_BODY_FAT)}
            </Box>
          )}
          <Box
            component="button"
            onClick={() => canCreate && navigate("/app/company-measurements/new", {state: {from: view}})}
            disabled={!canCreate}
            title={!canCreate ? "בחר חברה כדי ליצור מדד" : ""}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
              fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
              border: "none", cursor: canCreate ? "pointer" : "not-allowed", fontFamily: "inherit",
              transition: "all .14s",
              "&:hover": canCreate ? { background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)" } : {},
              "&:disabled": { background: t.border, color: t.t3, cursor: "not-allowed" },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            מדד חדש
          </Box>
        </Box>
        )}
      </Box>

      {/* View tabs */}
      <Box sx={{ display: "flex", gap: "8px", direction: "rtl" }}>
        {VIEWS.map(({ key, label, Icon }) => (
          <Box key={key} component="button" onClick={() => setView(key)} sx={tabBtn(view === key)}>
            <Icon sx={{ fontSize: 16 }} />
            {label}
          </Box>
        ))}
        {savingOrder && (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", color: t.t3, fontSize: "12px" }}>
            <CircularProgress size={13} sx={{ color: t.green }} /> שומר סדר…
          </Box>
        )}
      </Box>

      {/* ===== כללי (ALL) — catalog table ===== */}
      {!isGenderView && (
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden", minHeight: 200,
        }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress size={32} sx={{ color: t.green }} />
            </Box>
          ) : catalogEmpty ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px" }}>
              <Typography sx={{ color: t.t3, fontSize: "15px", fontWeight: 500 }}>אין מדדי גוף</Typography>
              <Typography sx={{ color: t.t3, fontSize: "13px" }}>לחץ על "מדד חדש" כדי להוסיף</Typography>
            </Box>
          ) : (
            <Box sx={{ padding: "12px 14px", direction: "rtl", display: "flex", flexDirection: "column", gap: "6px" }}>
              {catalogRows.map(({ def, hidden: isHidden }) => (
                <CatalogRow
                  key={`${isHidden ? "h" : "a"}-${def.id}`}
                  def={def}
                  hidden={isHidden}
                  lockReason={formulaLockReason(def, lockedByFormula)}
                  onEdit={() => navigate(`/app/company-measurements/${def.id}`, {state: {from: view}})}
                  onHide={() => setDeleteDialog({ open: true, definition: def })}
                  onRestore={() => handleRestore(def)}
                  onPurge={() => setPurgeDialog({ open: true, definition: def, busy: false })}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ===== Gender view (MALE / FEMALE) — arrangement grid ===== */}
      {isGenderView && (
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden", minHeight: 200,
        }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress size={32} sx={{ color: t.green }} />
            </Box>
          ) : gridEmpty ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px" }}>
              <Typography sx={{ color: t.t3, fontSize: "15px", fontWeight: 500 }}>
                אין מדדים עבור {genderLabel(gender)}
              </Typography>
              <Typography sx={{ color: t.t3, fontSize: "13px" }}>הוסף מדד קיים מהרשימה למטה, או צור מדד חדש בלשונית כללי</Typography>
            </Box>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={grid.map((it) => it.id)} strategy={rectSortingStrategy}>
                <Box sx={{
                  padding: "14px", direction: "rtl",
                  display: "grid", gap: "10px",
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                }}>
                  {grid.map((item, index) => (
                    <SortableMeasurementCard
                      key={item.id}
                      item={item}
                      index={index}
                      gender={gender}
                      otherGender={otherGender}
                      both={isBoth(item)}
                      lockReason={gridLockReason(item)}
                      onEdit={() => navigate(`/app/company-measurements/${item.id}`, {state: {from: view}})}
                      onToggleGender={() => handleToggleOtherGender(item)}
                      onRemove={() => handleRemoveFromGrid(item)}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          )}
        </Box>
      )}

      {/* Not-in-this-gender section — active measurements missing from the current gender's grid
          (i.e. they're only in the other gender). One click adds them back to this gender. */}
      {isGenderView && !loading && notInThisGender.length > 0 && (
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
        }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`, background: t.bg, direction: "rtl",
          }}>
            {gender === "MALE" ? <MaleIcon sx={{ fontSize: 15, color: t.t3 }} /> : <FemaleIcon sx={{ fontSize: 15, color: t.t3 }} />}
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: t.t2 }}>
              לא משויך ל{genderLabel(gender)} ({notInThisGender.length})
            </Typography>
            <Typography sx={{ fontSize: "11.5px", color: t.t3 }}>
              — מדדים שמשויכים ל{genderLabel(otherGender)} בלבד. הוסף כדי להציג גם ל{genderLabel(gender)}.
            </Typography>
          </Box>
          <Box sx={{ padding: "10px 14px", direction: "rtl", display: "flex", flexDirection: "column", gap: "6px" }}>
            {notInThisGender.map((def) => (
              <Box
                key={def.id}
                sx={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "8px 10px", borderRadius: t.rs, background: t.bg, border: `1px solid ${t.borderLight}`,
                }}
              >
                <Avatar src={imageUrl(def.imageUrl, 30)} sx={{ width: 30, height: 30, bgcolor: t.border, color: t.t3 }} variant="rounded">
                  <MeasurementIcon sx={{ fontSize: 15 }} />
                </Avatar>
                <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: t.t2, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {def.nameHeb || def.name}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: t.t3 }}>{unitLabel(def.unit)}</Typography>
                <Tooltip title={`הוסף ל${genderLabel(gender)}`}>
                  <Box
                    component="button"
                    onClick={() => handleAddToGender(def)}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: "4px",
                      background: t.greenLight, color: t.greenText, height: 30, px: "12px", borderRadius: t.rs,
                      fontSize: "12px", fontWeight: 600, border: `1px solid rgba(1,187,167,.25)`,
                      cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
                      "&:hover": { background: t.green, color: "#fff" },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 15 }} />
                    הוסף ל{genderLabel(gender)}
                  </Box>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Permanent-delete confirmation for a hidden measurement (RTL inside Dialog — Portal) */}
      <Dialog
        open={purgeDialog.open}
        onClose={() => (purgeDialog.busy ? undefined : setPurgeDialog({ open: false, definition: null, busy: false }))}
        PaperProps={{ sx: { borderRadius: t.r, minWidth: { xs: "92%", sm: 440 }, padding: "6px" } }}
      >
        <RTL>
          <Box dir="rtl" sx={{ padding: "20px 22px" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: t.text, mb: 0.5 }}>
              מחיקת מדד לצמיתות
            </Typography>
            <Typography sx={{ fontSize: 14, color: t.t2, mb: 2 }}>
              {`"${purgeDialog.definition?.nameHeb || purgeDialog.definition?.name || ""}"`}
            </Typography>
            <Box sx={{
              display: "flex", alignItems: "flex-start", gap: 1, mb: 2,
              background: t.redLight, borderRadius: t.rs, padding: "12px 14px",
            }}>
              <DeleteForeverIcon sx={{ fontSize: 20, color: t.red, mt: "1px" }} />
              <Typography sx={{ fontSize: 13.5, color: t.text, lineHeight: 1.5 }}>
                פעולה זו תמחק את המדד ואת <b>כל הנתונים</b> שנרשמו עבורו אצל כל המתאמנים.
                <b> לא ניתן לשחזר לאחר מכן.</b>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <Box
                component="button"
                onClick={() => setPurgeDialog({ open: false, definition: null, busy: false })}
                disabled={purgeDialog.busy}
                sx={{
                  flex: 1, height: 40, borderRadius: t.rs, border: `1.5px solid ${t.border}`,
                  background: t.white, color: t.t2, fontSize: 13.5, fontWeight: 600,
                  cursor: purgeDialog.busy ? "not-allowed" : "pointer", fontFamily: "inherit",
                }}
              >
                ביטול
              </Box>
              <Box
                component="button"
                onClick={handlePurge}
                disabled={purgeDialog.busy}
                sx={{
                  flex: 1, height: 40, borderRadius: t.rs, border: "none",
                  background: t.red, color: "#fff", fontSize: 13.5, fontWeight: 700,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px",
                  cursor: purgeDialog.busy ? "not-allowed" : "pointer", fontFamily: "inherit",
                  opacity: purgeDialog.busy ? 0.7 : 1,
                }}
              >
                {purgeDialog.busy ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <DeleteForeverIcon sx={{ fontSize: 18 }} />
                )}
                מחק לצמיתות
              </Box>
            </Box>
          </Box>
        </RTL>
      </Dialog>
    </Box>
  );
};

export default MeasurementDefinitionsScreen;
